import { useTranslation } from "react-i18next";

import { PaymentMethodDisplay } from "checkout-experience/components/payment-method/payment-method-summary-display";
import { getAccountType } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/get-account-type";
import { getCardIcon } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/get-card-icon";
import { getCardIconForPaze } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/get-card-icon-for-paze";
import { getPayByBankAccountTypeString } from "checkout-experience/utils/payment-helpers";
import { PaymentType } from "checkout-query-client/types/form-types";

import { EcpBankAccountType } from "frontend-client";

export const useTitleForPaymentSummary = (
  paymentMethodSummary: PaymentMethodDisplay
) => {
  const {
    paymentType,
    network,
    last4,
    ddaAccountNumberLast4,
    bankAccountType,
    ibanLast4,
    bankName,
    accountType,
    last4digitsOfAccountNumber,
  } = paymentMethodSummary;
  const { t } = useTranslation("combined");
  const CardIcon =
    paymentType === PaymentType.paze
      ? getCardIconForPaze(network)
      : getCardIcon(network);
  const type = getAccountType(bankAccountType);
  switch (paymentType) {
    case PaymentType.creditCard:
      return (
        t("creditCardEnding", {
          number: last4,
          cardNetwork: CardIcon
            ? t(`checkoutForm.cardNumber.cardNames.${CardIcon}`)
            : "",
        }) ?? ""
      );
    case PaymentType.eCheck:
      return ddaAccountNumberLast4
        ? (t("eCheckAccountNumber", {
            number: ddaAccountNumberLast4,
            accountType:
              type === EcpBankAccountType.UNSPECIFIED
                ? ""
                : t(`accountType.${type as string}`),
          }) ?? "")
        : t("eCheck");
    case PaymentType.sepa:
      return ibanLast4
        ? (t("sepaAccountNumber", { number: ibanLast4 }) ?? "")
        : t("sepa");
    case PaymentType.googlePay:
      return last4 && network
        ? (t("googlePay.googlePayWithCardData", {
            number: last4,
            cardNetwork: CardIcon
              ? t(`checkoutForm.cardNumber.cardNames.${CardIcon}`)
              : "",
          }) ?? "")
        : (t("googlePay.googlePay") ?? "");
    case PaymentType.applePay:
      return last4
        ? (t("applePay.applePayWithCardData", {
            cardData: last4,
          }) ?? "")
        : (t("applePay.applePay") ?? "");
    case PaymentType.paze:
      return last4
        ? ` ${t("paze.paze")} ${t("creditCardEnding", {
            number: last4,
            cardNetwork: CardIcon
              ? t(`checkoutForm.cardNumber.cardNames.${CardIcon}`)
              : "",
          })}`
        : t("paze.paze");
    case PaymentType.ideal:
      return t("ideal");
    case PaymentType.alipay:
      return t("alipay");
    case PaymentType.wechatPay:
      return t("wechatPay");
    case PaymentType.trustly:
      return t("trustly");
    case PaymentType.paypal:
      return t("paypal");
    case PaymentType.payByBank:
      return (
        `${bankName ? bankName + " " : ""}${accountType ? getPayByBankAccountTypeString(accountType) : ""}` +
        `${t("payByBank.accountWithLast4", {
          last4: last4digitsOfAccountNumber,
        })}`
      );
    case PaymentType.giftCard:
      return (
        t("giftCardEnding", {
          number: last4,
        }) ?? ""
      );
    default:
      return undefined;
  }
};
